<template>
	<div
		v-if="ready"
		data-testid="help-assistant-panel"
		class="help-assistant-panel"
	>
		<div class="help-assistant-panel__header">
			<button
				type="button"
				data-testid="help-assistant-chats-toggle"
				:aria-expanded="showChatList ? 'true' : 'false'"
				class="help-assistant-panel__chats-toggle"
				@click="showChatList = !showChatList"
			>
				<lucide-icon
					name="History"
					:size="16"
				/>
				Chats
			</button>
		</div>

		<help-chat-list
			v-if="showChatList"
			:chats="chats"
			:active-chat-id="chatId"
			:loading="chatsLoading"
			:disabled="streaming"
			@new-chat="handleNewChat"
			@select="handleSelectChat"
			@delete="handleDeleteChat"
		/>

		<help-message-list
			:messages="messages"
			:streaming="streaming"
			:tool-activity="toolActivity"
			:ratings="ratings"
			@retry="handleRetry"
			@rate="handleRate"
			@regenerate="handleRegenerate"
			@followup="handleSend"
		/>

		<div
			v-if="pendingActions.length > 0"
			data-testid="help-assistant-pending-actions"
			class="help-assistant-panel__actions"
		>
			<help-action-card
				v-for="action in pendingActions"
				:key="action.actionId"
				:action="action"
				@confirm="handleConfirmAction"
				@dismiss="handleDismissAction"
			/>
		</div>

		<help-composer
			:streaming="streaming"
			@send="handleSend"
			@stop="handleStop"
		/>
	</div>
</template>

<script>
import { LucideIcon } from '@syrenis/cassie-material-design'
import HelpChatList from './help-chat-list.vue'
import HelpMessageList from './help-message-list.vue'
import HelpActionCard from './help-action-card.vue'
import HelpComposer from './help-composer.vue'
import { createHelpApiClient } from './utils/help-api-client.js'
import { streamHelpChat } from './utils/sse-client.js'
import { runTurn, retryTurn, nextUid } from './utils/run-turn.js'
import { ratingsFromMessages } from './utils/chat-core.js'
import { getAuthToken } from '../../../state/modules/auth.js'
import { getEnvOptional } from '../../../../../shared/utils/runtime-settings.js'

const CHAT_STREAM_PATH = '/api/help/chat'
// How long a resolved action card (applied/error/dismissed) stays visible before it's removed
// from pendingActions, so the outcome is readable rather than vanishing the instant it resolves
// (AB#26561 PR #2723 review).
const ACTION_REMOVAL_DELAY_MS = 4000

/**
 * Help-assistant panel orchestrator (AB#26561 Task 8) - a thin shell composing the four
 * presentational children (help-chat-list, help-message-list, help-action-card,
 * help-composer) over the framework-free `run-turn.js` state machine and the
 * help-api-client.js / sse-client.js utils. Holds no business logic of its own beyond wiring:
 * every rule about how a turn streams, splits, persists, errors or retries lives in run-turn.js
 * and chat-core.js, ported for fidelity from the React reference
 * (syrenis-frontends/packages/syrenis-ui/src/components/HelpAssistant/HelpAssistant.tsx).
 *
 * Renders nothing (and warns once) when VUE_APP_BASE_URL_PLATFORMAGENT isn't configured - a
 * missing base URL is an ops/config error, not something to show the end user a broken panel
 * over.
 */
export default {
	name: 'help-assistant-panel',
	components: {
		LucideIcon,
		HelpChatList,
		HelpMessageList,
		HelpActionCard,
		HelpComposer
	},
	props: {
		/** Screen context threaded through to the stream request and thumbs feedback, so the
		 * server/analytics know which part of the product the question was asked from. */
		context: {
			type: String,
			default: '',
			validator: value => value.length <= 300
		}
	},
	data () {
		return {
			baseUrl: '',
			api: null,
			chats: [],
			chatsLoading: false,
			chatId: null,
			messages: [],
			streaming: false,
			toolActivity: null,
			pendingActions: [],
			// actionId -> setTimeout id, for the resolved-card removal scheduled in
			// handleConfirmAction/handleDismissAction. Tracked so beforeDestroy and any other
			// pendingActions reset (openChat, handleRetry) can clear a still-pending timeout
			// rather than leaving it to fire against state that's moved on.
			actionTimeouts: {},
			ratings: {},
			abortController: null,
			showChatList: false,
			// Set the moment the user does anything (send/new/select/delete): guards the
			// mount-time auto-resume sequence from clobbering whatever the user has since
			// started, if it's still awaiting a response when one of those fires.
			userActed: false
		}
	},
	computed: {
		ready () {
			return this.baseUrl !== ''
		}
	},
	created () {
		this.baseUrl = getEnvOptional('VUE_APP_BASE_URL_PLATFORMAGENT')
		if (!this.baseUrl) {
			// Config missing is an ops error, not a user-facing one - warn once and render
			// nothing rather than showing a panel that can never talk to the server.
			console.warn('help-assistant-panel: VUE_APP_BASE_URL_PLATFORMAGENT is not configured; the help assistant will not render.')
			return
		}
		this.api = createHelpApiClient({ baseUrl: this.baseUrl, getToken: getAuthToken })
		this.initialize()
	},
	beforeDestroy () {
		this.abortController?.abort()
		this.clearActionTimeouts()
	},
	methods: {
		clearActionTimeouts () {
			Object.values(this.actionTimeouts).forEach(id => clearTimeout(id))
			this.actionTimeouts = {}
		},
		scheduleActionRemoval (actionId) {
			if (this.actionTimeouts[actionId]) clearTimeout(this.actionTimeouts[actionId])
			this.actionTimeouts[actionId] = setTimeout(() => {
				this.pendingActions = this.pendingActions.filter(action => action.actionId !== actionId)
				delete this.actionTimeouts[actionId]
			}, ACTION_REMOVAL_DELAY_MS)
		},
		/** Builds a fresh `deps` object for run-turn.js per call, so a changed `context` prop
		 * and a freshly-read auth token are always current rather than captured once at
		 * `created`. */
		buildDeps () {
			return {
				context: this.context || undefined,
				api: this.api,
				stream: options => streamHelpChat({
					endpoint: `${this.baseUrl}${CHAT_STREAM_PATH}`,
					headers: { authorization: `Bearer ${getAuthToken()}` },
					...options
				})
			}
		},
		async loadChats () {
			this.chatsLoading = true
			try {
				this.chats = await this.api.listChats()
			} catch {
				this.chats = []
			} finally {
				this.chatsLoading = false
			}
		},
		async openChat (chatId) {
			let detail
			try {
				detail = await this.api.getChat(chatId)
			} catch {
				// A non-404 failure (network error, 5xx): leave whatever chat was already open
				// alone rather than letting this reject uncaught.
				return
			}
			if (!detail) return
			// A confirmable action card (and any tool-activity hint) from whatever chat was
			// previously open must not survive the switch - it belongs to a turn in a different
			// conversation, and confirming it here would apply a write action against the wrong
			// context (AB#26561 PR #2723 review: cross-chat action leak).
			this.clearActionTimeouts()
			this.pendingActions = []
			this.toolActivity = null
			this.chatId = detail.chatId
			this.messages = detail.messages.map(message => ({
				role: message.role,
				content: message.content,
				id: message.messageId,
				uid: nextUid(),
				timestamp: message.createdAt
			}))
			this.ratings = ratingsFromMessages(detail.messages)
		},
		resetToBlank () {
			this.clearActionTimeouts()
			this.chatId = null
			this.messages = []
			this.ratings = {}
			this.pendingActions = []
			this.toolActivity = null
		},
		/** Auto-resume on mount: load the chat list, then resume the most recently updated chat.
		 * Bails if the user has meanwhile acted, so a stale resume can't overwrite a chat/turn
		 * they've already started themselves. */
		async initialize () {
			await this.loadChats()
			if (this.userActed) return
			if (this.chats.length > 0) {
				// Resume the most recently updated chat - sorted defensively rather than
				// trusting the server's response order, since that isn't part of the contract.
				const [mostRecent] = this.chats.slice().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
				await this.openChat(mostRecent.chatId)
			}
		},
		async handleSend ({ text }) {
			this.userActed = true
			const hadChatId = Boolean(this.chatId)
			await runTurn({ state: this, deps: this.buildDeps(), userText: text })
			// The chat list fetched earlier doesn't know about a chat minted just now - refresh
			// so the switcher shows it (real title, updated time) once this turn lands.
			if (!hadChatId && this.chatId) this.loadChats()
		},
		handleRetry ({ index }) {
			this.userActed = true
			// Action cards don't carry turn identity, so there's no reliable way to tell which
			// pending action(s) belong to the turn being retried - clear all of them rather than
			// risk leaving a stale card behind, or a user confirming one against a turn that
			// retryTurn just dropped (AB#26561 PR #2723 review: coarse-clear is the safe option).
			this.clearActionTimeouts()
			this.pendingActions = []
			return retryTurn({ state: this, deps: this.buildDeps(), index })
		},
		handleRegenerate () {
			const index = this.messages.length - 1
			// Mirrors the React reference's regenerateLast: a regenerated answer supersedes
			// whatever thumbs rating was given to the old one, so drop the stale entry rather
			// than leaving it keyed to an answer that no longer exists. Ratings are keyed by
			// the server-assigned messageId, so this only applies once the turn has one.
			const staleMessageId = this.messages[index]?.id
			if (staleMessageId !== undefined) this.$delete(this.ratings, staleMessageId)
			return this.handleRetry({ index })
		},
		handleStop () {
			this.abortController?.abort()
		},
		handleNewChat () {
			this.userActed = true
			this.resetToBlank()
		},
		async handleSelectChat ({ chatId }) {
			this.userActed = true
			await this.openChat(chatId)
		},
		async handleDeleteChat ({ chatId }) {
			this.userActed = true
			const wasActive = chatId === this.chatId
			try {
				await this.api.deleteChat(chatId)
			} catch {
				return
			}
			this.chats = this.chats.filter(chat => chat.chatId !== chatId)
			if (wasActive) this.resetToBlank()
		},
		async handleRate ({ messageId, rating }) {
			if (!this.chatId || messageId === undefined || this.ratings[messageId]) return
			this.$set(this.ratings, messageId, rating)
			const accepted = await this.api.sendFeedback(this.chatId, { messageId, rating, screenContext: this.context })
			if (!accepted) this.$delete(this.ratings, messageId)
		},
		async handleConfirmAction ({ actionId }) {
			const action = this.pendingActions.find(item => item.actionId === actionId)
			if (!action) return
			this.$set(action, 'status', 'confirming')
			const applied = await this.api.confirmAction(actionId)
			this.$set(action, 'status', applied ? 'applied' : 'error')
			this.scheduleActionRemoval(actionId)
		},
		handleDismissAction ({ actionId }) {
			const action = this.pendingActions.find(item => item.actionId === actionId)
			if (!action) return
			this.$set(action, 'status', 'dismissed')
			this.api.dismissAction(actionId)
			this.scheduleActionRemoval(actionId)
		}
	}
}
</script>

<style lang="scss" scoped>
.help-assistant-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--card, #fff);
  border: 1px solid var(--border, #dfe4ea);
  border-radius: var(--radius, 8px);

  &__header {
    display: flex;
    justify-content: flex-end;
    padding: 8px;
    border-bottom: 1px solid var(--border, #dfe4ea);
  }

  &__chats-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    min-height: 24px;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: var(--muted-foreground, #64748b);
    cursor: pointer;
    background: none;
    border: none;
    border-radius: var(--radius-sm, 4px);
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover {
      color: var(--foreground, #4a5568);
      background-color: var(--muted, #f5f5f5);
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0 16px 12px;
  }
}
</style>
